import { When } from 'react-if';
import { Checkbox } from '@zep-us/zus';
import { Gray200, Violet300 } from '@zep-us/zus/tokens';
import { MathText } from '@/shared/features/rich-text';
import { cn } from '@/utils/tailwind.util';
import type { StandardCardProps } from '../types';
import { StandardCardBadges } from './StandardCardBadges';
import { StandardCardMetadata } from './StandardCardMetadata';

// Design tokens from Figma
const CARD_BORDER_SELECTED = `shadow-[inset_0_0_0_2px_${Violet300}]`;
const CARD_BORDER_UNSELECTED = `shadow-[inset_0_0_0_1px_${Gray200}]`;

/**
 * Achievement Standard Card Component (Redesigned).
 *
 * Layout:
 * - Top: Badge area (code badge + optional score badge)
 * - Middle: Title (standard text) + Description (explanation) - multi-line support
 * - Bottom: Metadata footer (grade | subject | area)
 * - Right: Checkbox
 *
 * Design:
 * - Selected: shadow-[inset_0_0_0_2px_#A49BFF] (violet border)
 * - Unselected: shadow-[inset_0_0_0_1px_#E9EAF2] (gray border)
 *
 *
 * @example
 * <StandardCard
 *   standard={standard}
 *   isSelected={isSelected}
 *   onToggle={handleToggle}
 *   showScore={true} // for recommendation tab
 * />
 */
export const StandardCard = ({
  standard,
  isSelected,
  onToggle,
  disabled = false,
  showScore = false
}: StandardCardProps) => {
  return <div className={cn('cursor-pointer rounded-md p-lg flex gap-md bg-white shadow-xs', isSelected ? CARD_BORDER_SELECTED : CARD_BORDER_UNSELECTED)} onClick={() => onToggle(standard)} data-sentry-component="StandardCard" data-sentry-source-file="StandardCard.tsx">
      {/* Left: Content area */}
      <div className="flex min-w-0 flex-1 flex-col gap-sm">
        {/* Top: Badges */}
        <StandardCardBadges code={standard.code} score={standard.score} showScore={showScore} data-sentry-element="StandardCardBadges" data-sentry-source-file="StandardCard.tsx" />

        {/* Middle: Title + Description (grouped with 6px gap) */}
        <div className="flex flex-col gap-2xs">
          <MathText as="h4" className="text-heading-sm-bold text-default" text={standard.standard} data-sentry-element="MathText" data-sentry-source-file="StandardCard.tsx" />

          <When condition={!!standard.explanation} data-sentry-element="When" data-sentry-source-file="StandardCard.tsx">
            <MathText as="p" className="text-body-sm-medium text-neutral" text={standard.explanation ?? ''} data-sentry-element="MathText" data-sentry-source-file="StandardCard.tsx" />
          </When>
        </div>

        {/* Bottom: Metadata footer */}
        <StandardCardMetadata grade={standard.grade} subject={standard.subject} area={standard.area} data-sentry-element="StandardCardMetadata" data-sentry-source-file="StandardCard.tsx" />
      </div>

      {/* Right: Checkbox */}
      <div className="flex items-start pt-1">
        <Checkbox checked={isSelected} disabled={disabled} data-sentry-element="Checkbox" data-sentry-source-file="StandardCard.tsx" />
      </div>
    </div>;
};